/**
 * Generato automaticamente.
 * procedimenti-authentication-manager
 */
import { useMutation, useQuery } from 'react-query';
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { apiClientRequest } from '@framework/api-client';
import type { ErrorType } from '@framework/api-client';
import type {
  GetOpenwebMetodoIdpAuthReqParams,
  LoginResponse,
  PostOpenwebMetodoIdpAssertionconsumerParams,
} from '.././schemas';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

// eslint-disable-next-line
type SecondParameter<T extends (...args: any) => any> = T extends (
  config: any,
  args: infer P,
) => any
  ? P
  : never;

/**
 * Callback di validazione asserzione risposta dall'IDP
 * @summary Callback di validazione asserzione risposta dall'IDP
 */
export const postOpenwebMetodoIdpAssertionconsumer = (
  metodoIdp: string,
  params: PostOpenwebMetodoIdpAssertionconsumerParams,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<void>(
    {
      url: `/procedimenti-authentication-manager/openweb/${metodoIdp}/assertionconsumer`,
      method: 'post',
      params,
    },
    options,
  );
};

export const getPostOpenwebMetodoIdpAssertionconsumerMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postOpenwebMetodoIdpAssertionconsumer>>,
    TError,
    { metodoIdp: string; params: PostOpenwebMetodoIdpAssertionconsumerParams },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postOpenwebMetodoIdpAssertionconsumer>>,
  TError,
  { metodoIdp: string; params: PostOpenwebMetodoIdpAssertionconsumerParams },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postOpenwebMetodoIdpAssertionconsumer>>,
    { metodoIdp: string; params: PostOpenwebMetodoIdpAssertionconsumerParams }
  > = props => {
    const { metodoIdp, params } = props ?? {};

    return postOpenwebMetodoIdpAssertionconsumer(
      metodoIdp,
      params,
      requestOptions,
    );
  };

  return { mutationFn, ...mutationOptions };
};

export type PostOpenwebMetodoIdpAssertionconsumerMutationResult = NonNullable<
  Awaited<ReturnType<typeof postOpenwebMetodoIdpAssertionconsumer>>
>;

export type PostOpenwebMetodoIdpAssertionconsumerMutationError =
  ErrorType<unknown>;

/**
 * @summary Callback di validazione asserzione risposta dall'IDP
 */
export const usePostOpenwebMetodoIdpAssertionconsumer = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postOpenwebMetodoIdpAssertionconsumer>>,
    TError,
    { metodoIdp: string; params: PostOpenwebMetodoIdpAssertionconsumerParams },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPostOpenwebMetodoIdpAssertionconsumerMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * effettua il login restituendo il token jwt sulla base del cookie presente sulla request
 * @summary effettua il login restituendo il token jwt sulla base del cookie presente sulla request
 */
export const postOpenwebJwtLogin = (
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<LoginResponse>(
    {
      url: `/procedimenti-authentication-manager/openweb/jwt/login`,
      method: 'post',
    },
    options,
  );
};

export const getPostOpenwebJwtLoginMutationOptions = <
  TError = ErrorType<unknown>,
  TVariables = void,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postOpenwebJwtLogin>>,
    TError,
    TVariables,
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postOpenwebJwtLogin>>,
  TError,
  TVariables,
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postOpenwebJwtLogin>>,
    TVariables
  > = () => {
    return postOpenwebJwtLogin(requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostOpenwebJwtLoginMutationResult = NonNullable<
  Awaited<ReturnType<typeof postOpenwebJwtLogin>>
>;

export type PostOpenwebJwtLoginMutationError = ErrorType<unknown>;

/**
 * @summary effettua il login restituendo il token jwt sulla base del cookie presente sulla request
 */
export const usePostOpenwebJwtLogin = <
  TError = ErrorType<unknown>,
  TVariables = void,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postOpenwebJwtLogin>>,
    TError,
    TVariables,
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPostOpenwebJwtLoginMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Restituisce l’xml dei metadati del SP configurato
 * @summary Restituisce l’xml dei metadati del SP configurato
 */
export const getOpenwebMetodoIdpMetadata = (
  metodoIdp: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<string>(
    {
      url: `/procedimenti-authentication-manager/openweb/${metodoIdp}/metadata`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetOpenwebMetodoIdpMetadataQueryKey = (metodoIdp: string) =>
  [
    `/procedimenti-authentication-manager/openweb/${metodoIdp}/metadata`,
  ] as const;

export const getGetOpenwebMetodoIdpMetadataQueryOptions = <
  TData = Awaited<ReturnType<typeof getOpenwebMetodoIdpMetadata>>,
  TError = ErrorType<unknown>,
>(
  metodoIdp: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getOpenwebMetodoIdpMetadata>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getOpenwebMetodoIdpMetadata>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetOpenwebMetodoIdpMetadataQueryKey(metodoIdp);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getOpenwebMetodoIdpMetadata>>
  > = ({ signal }) =>
    getOpenwebMetodoIdpMetadata(metodoIdp, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!metodoIdp, ...queryOptions };
};

export type GetOpenwebMetodoIdpMetadataQueryResult = NonNullable<
  Awaited<ReturnType<typeof getOpenwebMetodoIdpMetadata>>
>;
export type GetOpenwebMetodoIdpMetadataQueryError = ErrorType<unknown>;

/**
 * @summary Restituisce l’xml dei metadati del SP configurato
 */
export const useGetOpenwebMetodoIdpMetadata = <
  TData = Awaited<ReturnType<typeof getOpenwebMetodoIdpMetadata>>,
  TError = ErrorType<unknown>,
>(
  metodoIdp: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getOpenwebMetodoIdpMetadata>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetOpenwebMetodoIdpMetadataQueryOptions(
    metodoIdp,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Inizia il processo di SSO ed effettua il redirect verso l'IDP
 * @summary Inizia il processo di SSO ed effettua il redirect verso l'IDP
 */
export const getOpenwebMetodoIdpAuthReq = (
  metodoIdp: string,
  params?: GetOpenwebMetodoIdpAuthReqParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<void>(
    {
      url: `/procedimenti-authentication-manager/openweb/${metodoIdp}/auth-req`,
      method: 'get',
      params,
      signal,
    },
    options,
  );
};

export const getGetOpenwebMetodoIdpAuthReqQueryKey = (
  metodoIdp: string,
  params?: GetOpenwebMetodoIdpAuthReqParams,
) =>
  [
    `/procedimenti-authentication-manager/openweb/${metodoIdp}/auth-req`,
    ...(params ? [params] : []),
  ] as const;

export const getGetOpenwebMetodoIdpAuthReqQueryOptions = <
  TData = Awaited<ReturnType<typeof getOpenwebMetodoIdpAuthReq>>,
  TError = ErrorType<unknown>,
>(
  metodoIdp: string,
  params?: GetOpenwebMetodoIdpAuthReqParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getOpenwebMetodoIdpAuthReq>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getOpenwebMetodoIdpAuthReq>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetOpenwebMetodoIdpAuthReqQueryKey(metodoIdp, params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getOpenwebMetodoIdpAuthReq>>
  > = ({ signal }) =>
    getOpenwebMetodoIdpAuthReq(metodoIdp, params, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!metodoIdp, ...queryOptions };
};

export type GetOpenwebMetodoIdpAuthReqQueryResult = NonNullable<
  Awaited<ReturnType<typeof getOpenwebMetodoIdpAuthReq>>
>;
export type GetOpenwebMetodoIdpAuthReqQueryError = ErrorType<unknown>;

/**
 * @summary Inizia il processo di SSO ed effettua il redirect verso l'IDP
 */
export const useGetOpenwebMetodoIdpAuthReq = <
  TData = Awaited<ReturnType<typeof getOpenwebMetodoIdpAuthReq>>,
  TError = ErrorType<unknown>,
>(
  metodoIdp: string,
  params?: GetOpenwebMetodoIdpAuthReqParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getOpenwebMetodoIdpAuthReq>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetOpenwebMetodoIdpAuthReqQueryOptions(
    metodoIdp,
    params,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};
